import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, User, Users } from 'lucide-react';
import StatusBadge from './StatusBadge';
import PriorityBadge from './PriorityBadge';

const TaskCard = ({ task, onTaskClick, userRole = 'admin', showAssignedDeadline = false }) => {
  const navigate = useNavigate();

  const handleClick = () => {
    if (onTaskClick) {
      onTaskClick(task);
    } else {
      const basePath = userRole === 'admin' ? '/admin/tasks' : '/employee/tasks';
      navigate(`${basePath}/${task._id}`);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return null;
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return null;
    return new Date(dateString).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const isOverdue = task.dueDate && new Date(task.dueDate) < new Date() && task.status !== 'completed';
  const hasUnreadComments = task.comments && task.comments.length > 0;

  return (
    <div 
      onClick={handleClick}
      className="bg-white rounded-lg shadow hover:shadow-md transition-shadow cursor-pointer border border-gray-200 overflow-hidden"
    >
      <div className="p-5">
        {/* Header */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1 min-w-0">
            <h3 className="text-lg font-semibold text-gray-900 truncate">
              {task.title}
            </h3>
            <p className="text-sm text-gray-600 mt-1 line-clamp-2">
              {task.description}
            </p>
          </div>
          <div className="ml-3 flex-shrink-0">
            <PriorityBadge priority={task.priority} />
          </div>
        </div>

        {/* Assignee Info — hidden for employee view (task is always their own) */}
        {userRole !== 'employee' && (
        <div className="flex items-center text-sm text-gray-600 mb-3">
          {task.assignedTo && task.assignedTo.length > 0 && (
            <div className="flex items-center">
              {task.assignedTo.length === 1 ? (
                <>
                  <User className="h-4 w-4 mr-1" />
                  <span className="truncate">{task.assignedTo[0].name}</span>
                </>
              ) : (
                <>
                  <Users className="h-4 w-4 mr-1" />
                  <span>{task.assignedTo.length} assignees</span>
                </>
              )}
            </div>
          )}
        </div>
        )}

        {showAssignedDeadline && (
          <div className="mb-3 rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-700">
            <div className="flex items-center justify-between gap-3">
              <span className="font-medium text-gray-600">Assigned:</span>
              <span className="text-right font-semibold text-gray-900">
                {formatDateTime(task.createdAt) || '--'}
              </span>
            </div>
            <div className="mt-1 flex items-center justify-between gap-3">
              <span className="font-medium text-gray-600">Deadline:</span>
              <span className="text-right font-semibold text-gray-900">
                {formatDate(task.dueDate) || '--'}
              </span>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="flex items-center justify-between pt-3 border-t border-gray-200">
          <div className="flex items-center space-x-3">
            <StatusBadge status={task.status} reviewDecision={task.reviewDecision} />
            {isOverdue && (
              <span className="text-xs text-red-600 font-medium">Overdue</span>
            )}
          </div>
          
          <div className="flex items-center space-x-4 text-xs text-gray-500">
            {task.dueDate && !showAssignedDeadline && (
              <div className="flex items-center">
                <Calendar className="h-3 w-3 mr-1" />
                {formatDate(task.dueDate)}
              </div>
            )}
            {hasUnreadComments && (
              <div className="flex items-center">
                <div className="h-2 w-2 bg-blue-500 rounded-full mr-1"></div>
                {task.comments.length} comments
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TaskCard;
